import React, { useState, useCallback, useEffect, useContext } from 'react';
import PriceChart from './components/PriceChart';
import TechnicalPanel from './components/TechnicalPanel';
import FundamentalPanel from './components/FundamentalPanel';
import NewsPanel from './components/NewsPanel';
import VerdictPanel from './components/VerdictPanel';
import ScreenerPage from './components/ScreenerPage';
import WatchlistPage from './components/WatchlistPage';
import ComparePage from './components/ComparePage';
import AlertsPage from './components/AlertsPage';
import SearchBox from './components/SearchBox';
import { AuthContext } from './components/LoginGate';
import WatchStar from './components/WatchStar';
import { useQuote, useTechnical, useNews, useMarkets, useWatchlist } from './hooks/useData';
import { api } from './api';
import { changeColor, fmtPct, fmtBig, fmtPrice } from './components/UI';

const TABS = ['Grafico', 'Analisi Tecnica', 'Fondamentale', 'Notizie', 'Giudizio AI'];
const MARKET_ORDER = ['MIL', 'NYSE', 'NASDAQ', 'XETRA', 'PARIS', 'LSE'];
const MARKET_SHORT = {
  MIL: 'Milano', NYSE: 'NYSE', NASDAQ: 'NASDAQ',
  XETRA: 'XETRA', PARIS: 'Parigi', LSE: 'Londra', RAW: 'Altro',
};

function TickerRow({ item, market, active, onClick }) {
  const { data } = useQuote(item.ticker, market);
  const color = data?.change_pct ? changeColor(data.change_pct) : '#475569';
  const cur = data?.currency || 'EUR';
  return (
    <button onClick={onClick} style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      width: '100%', padding: '10px 16px 10px 24px', background: active ? '#1e2736' : 'transparent',
      border: 'none', borderLeft: `2px solid ${active ? '#3b82f6' : 'transparent'}`,
      cursor: 'pointer', textAlign: 'left',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: active ? '#e2e8f0' : '#94a3b8', fontFamily: "'IBM Plex Mono', monospace" }}>{item.ticker}</div>
        <div style={{ fontSize: 11, color: '#334155', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 }}>{item.name}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", color: '#e2e8f0' }}>
          {data?.price != null ? fmtPrice(data.price, cur) : '—'}
        </div>
        <div style={{ fontSize: 11, color }}>{data?.change_pct != null ? fmtPct(data.change_pct) : ''}</div>
      </div>
    </button>
  );
}

function MarketGroup({ code, marketData, selected, selectedMarket, onSelect, collapsed, onToggle }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px 6px', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.04em' }}>{marketData.label}</span>
        <span style={{ fontSize: 10, color: '#334155' }}>{collapsed ? '▸' : '▾'}</span>
      </div>
      {!collapsed && marketData.tickers.map(item => (
        <TickerRow key={`${code}:${item.ticker}`} item={item} market={code}
          active={selected === item.ticker && selectedMarket === code}
          onClick={() => onSelect(item.ticker, code)} />
      ))}
    </div>
  );
}

function QuoteHeader({ data, loading, watchStar }) {
  if (loading) return <div style={{ fontSize: 13, color: '#475569' }}>Caricamento dati di mercato…</div>;
  if (!data) return null;
  const color = changeColor(data.change_pct);
  const cur = data.currency || 'EUR';
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span className="quote-price" style={{ fontSize: 36, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: '#f1f5f9' }}>
          {fmtPrice(data.price, cur, 3)}
        </span>
        <span className="quote-change" style={{ fontSize: 16, fontWeight: 600, color }}>
          {data.change > 0 ? '+' : ''}{data.change?.toFixed(3)} ({fmtPct(data.change_pct)})
        </span>
        <span style={{ fontSize: 13, color: '#475569' }}>{data.name}</span>
        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: '#1a2030', color: '#64748b' }}>
          {MARKET_SHORT[data.market] || data.market} · {cur}
        </span>
        {watchStar}
      </div>
      <div className="quote-meta" style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
        {[
          { l: 'Vol.', v: data.volume ? `${(data.volume / 1e6).toFixed(1)}M` : '—' },
          { l: 'Market Cap', v: data.market_cap ? fmtBig(data.market_cap, cur) : '—' },
          { l: 'P/E', v: data.pe_ratio?.toFixed(1) || '—' },
          { l: 'Div.', v: data.dividend_yield ? `${data.dividend_yield}%` : '—' },
        ].map(({ l, v }) => (
          <div key={l} style={{ fontSize: 12 }}>
            <span style={{ color: '#475569' }}>{l}: </span>
            <span style={{ color: '#94a3b8', fontFamily: "'IBM Plex Mono', monospace" }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StockDetail({ ticker, market, watchlist }) {
  const [activeTab, setActiveTab] = useState('Grafico');
  const { data: quote, loading: qLoading } = useQuote(ticker, market);
  const { data: techData } = useTechnical(ticker, market);
  const { data: newsData } = useNews(ticker, quote?.name || '', market);
  const watched = watchlist.isWatched(ticker, market);
  const star = <WatchStar watched={watched} onToggle={() => watchlist.toggle({ ticker, market, name: quote?.name || ticker })} />;

  return (
    <>
      <QuoteHeader data={quote} loading={qLoading} watchStar={star} />
      <div className="tab-bar">
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            fontSize: 13, padding: '10px 16px', background: 'none', border: 'none',
            borderBottom: `2px solid ${activeTab === tab ? '#3b82f6' : 'transparent'}`,
            color: activeTab === tab ? '#60a5fa' : '#475569',
            fontWeight: activeTab === tab ? 600 : 400, cursor: 'pointer', marginBottom: -1,
          }}>{tab}</button>
        ))}
      </div>
      {activeTab === 'Grafico' && (
        <div className="card-responsive" style={{ background: '#131720', border: '1px solid #1e2736', borderRadius: 12, padding: '20px' }}>
          <PriceChart ticker={ticker} market={market} technicalData={techData} currency={quote?.currency} />
        </div>
      )}
      {activeTab === 'Analisi Tecnica' && <TechnicalPanel ticker={ticker} market={market} currency={quote?.currency} />}
      {activeTab === 'Fondamentale' && <FundamentalPanel ticker={ticker} market={market} quoteData={quote} />}
      {activeTab === 'Notizie' && <NewsPanel ticker={ticker} market={market} companyName={quote?.name || ''} />}
      {activeTab === 'Giudizio AI' && <VerdictPanel ticker={ticker} quoteData={quote} technicalData={techData} newsData={newsData} />}
    </>
  );
}

export default function App() {
  const { authRequired, logout } = useContext(AuthContext);
  const { data: markets, loading: marketsLoading } = useMarkets();
  const watchlist = useWatchlist();
  const [view, setView] = useState('home');
  const [selected, setSelected] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [compareStocks, setCompareStocks] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [search, setSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const openStock = useCallback((ticker, market) => {
    setSelected(ticker); setSelectedMarket(market); setView('detail'); setSidebarOpen(false);
  }, []);
  const openFromObj = useCallback((o) => openStock(o.ticker, o.market), [openStock]);
  const goHome = useCallback(() => { setView('home'); setSidebarOpen(false); }, []);
  const goWatchlist = useCallback(() => { setView('watchlist'); setSidebarOpen(false); }, []);
  const goCompare = useCallback((stocks) => { setCompareStocks(stocks || []); setView('compare'); setSidebarOpen(false); }, []);
  const goAlerts = useCallback(() => { setView('alerts'); setSidebarOpen(false); }, []);
  const toggleMarket = (code) => setCollapsed(c => ({ ...c, [code]: !c[code] }));

  // Controllo alert all'avvio + ogni 10 min
  useEffect(() => {
    const checkAlerts = async () => {
      try {
        const r = await api.alerts.check();
        if (r.triggered?.length > 0 && 'Notification' in window && Notification.permission === 'granted') {
          r.triggered.forEach(a => {
            new Notification('PSA — Alert prezzo', {
              body: `${a.ticker} ${a.type === 'price_above' ? 'sopra' : 'sotto'} ${a.value} (ora ${a.current_price})`,
            });
          });
        }
      } catch {}
    };
    if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission().catch(() => {});
    checkAlerts();
    const id = setInterval(checkAlerts, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const filterTickers = (tickers) => {
    if (!search.trim()) return tickers;
    const q = search.toLowerCase();
    return tickers.filter(t => t.ticker.toLowerCase().includes(q) || t.name.toLowerCase().includes(q));
  };

  const navBtn = (active) => ({
    fontSize: 13, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', flexShrink: 0,
    background: active ? '#1e3a5f' : 'transparent', color: active ? '#60a5fa' : '#64748b',
    border: `1px solid ${active ? '#2563eb40' : '#1e2736'}`, fontWeight: 500, whiteSpace: 'nowrap',
  });

  return (
    <div style={{ minHeight: '100vh', background: '#0a0c10', color: '#e2e8f0', fontFamily: "'Inter', sans-serif" }}>
      {/* ─── Top bar ─── */}
      <div className="topbar" style={{
        background: '#0d1117', borderBottom: '1px solid #1e2736', padding: '0 16px',
        display: 'flex', alignItems: 'center', gap: 12, height: 56,
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <button className="menu-toggle" onClick={() => setSidebarOpen(v => !v)}>☰</button>
        <span className="logo" onClick={goHome} style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, fontWeight: 600,
          color: '#60a5fa', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
        }}>PSA</span>

        <button className="nav-btn" style={navBtn(view === 'home')} onClick={goHome}><span className="nav-label">★ </span>Opportunità</button>
        <button className="nav-btn" style={navBtn(view === 'watchlist')} onClick={goWatchlist}>
          <span className="nav-label">☆ </span>Watchlist{watchlist.items.length ? ` (${watchlist.items.length})` : ''}
        </button>
        <button className="nav-btn" style={navBtn(view === 'compare')} onClick={() => goCompare(compareStocks)}><span className="nav-label">⇄ </span>Confronta</button>
        <button className="nav-btn" style={navBtn(view === 'alerts')} onClick={goAlerts}>🔔</button>

        <div className="search-wrap" style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <SearchBox onSelect={openFromObj} />
        </div>
        <span className="user-label" style={{ fontSize: 12, color: '#334155', fontFamily: "'IBM Plex Mono', monospace", flexShrink: 0 }}>
          Paolo Busolin
        </span>
        {authRequired && (
          <button onClick={logout} title="Esci da PSA" style={{
            fontSize: 12, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', flexShrink: 0,
            background: 'transparent', color: '#64748b', border: '1px solid #1e2736',
          }}>Esci</button>
        )}
      </div>

      {/* ─── Body ─── */}
      <div style={{ display: 'flex', height: 'calc(100vh - 56px)', position: 'relative' }}>
        {/* Overlay per chiudere sidebar su mobile */}
        <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

        {/* Sidebar */}
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} style={{
          width: 240, background: '#0d1117', borderRight: '1px solid #1e2736',
          padding: '12px 0', overflowY: 'auto', flexShrink: 0,
        }}>
          <div style={{ padding: '0 12px 12px' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filtra le liste…"
              style={{ width: '100%', fontSize: 13, padding: '10px 12px', background: '#131720', border: '1px solid #1e2736', borderRadius: 8, color: '#e2e8f0', outline: 'none' }} />
          </div>
          {marketsLoading && <div style={{ padding: 16, fontSize: 12, color: '#475569' }}>Caricamento…</div>}
          {markets && MARKET_ORDER.filter(c => markets[c]).map(code => {
            const md = { ...markets[code], tickers: filterTickers(markets[code].tickers) };
            if (search.trim() && md.tickers.length === 0) return null;
            return (
              <MarketGroup key={code} code={code} marketData={md}
                selected={selected} selectedMarket={selectedMarket} onSelect={openStock}
                collapsed={collapsed[code] && !search.trim()} onToggle={() => toggleMarket(code)} />
            );
          })}
        </aside>

        {/* Main content */}
        <main className="main-content" style={{ flex: 1, padding: '24px', overflowY: 'auto', maxWidth: 1100 }}>
          {view === 'home' && <ScreenerPage onOpen={openFromObj} />}
          {view === 'watchlist' && <WatchlistPage watchlist={watchlist} onOpen={openFromObj} onCompareAll={goCompare} />}
          {view === 'compare' && <ComparePage initialStocks={compareStocks} onOpen={openFromObj} />}
          {view === 'alerts' && <AlertsPage onOpen={openFromObj} />}
          {view === 'detail' && <StockDetail ticker={selected} market={selectedMarket} watchlist={watchlist} />}
        </main>
      </div>
    </div>
  );
}
